import React, { useRef, useEffect, useState } from 'react';
import { jsPDF } from 'jspdf';

const Invoice = ({ showBillModal, setShowBillModal, selectedBill }) => {
	const billRef = useRef();
	const [invoiceData, setInvoiceData] = useState(selectedBill || {});

	// Load invoice data from session storage
	useEffect(() => {
		const storedInvoice = sessionStorage.getItem('invoiceData');
		if (storedInvoice) {
			setInvoiceData(JSON.parse(storedInvoice));
		} else if (selectedBill) {
			sessionStorage.setItem('invoiceData', JSON.stringify(selectedBill));
		}
	}, [selectedBill]);

	// Generate PDF function
	const generatePDF = () => {
		const doc = new jsPDF({
			orientation: 'portrait',
			unit: 'mm',
			format: 'a4',
		});

		doc.html(billRef.current, {
			callback: (d) => d.save(`Invoice-NECTTOS-${invoiceData?.invoiceNumber}.pdf`),
			x: 10,
			y: 10,
			width: 190,
			windowWidth: billRef.current.scrollWidth, // Ensures proper width
			scale: 2,
		});
	};

	if (!showBillModal) return null;

	return (
		<div className='fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4'>
			<div className='bg-white w-full max-w-4xl rounded-lg shadow-lg overflow-hidden'>
				{/* Header Section */}
				<div className='flex justify-between items-center bg-gray-100 p-5 border-b'>
					<h2 className='text-xl font-bold text-gray-700'>Invoice</h2>
					<div className='space-x-2'>
						<button className='px-4 py-2 bg-blue-500 text-white rounded-md' onClick={generatePDF}>
							Download PDF
						</button>

						<button className='px-4 py-2 bg-red-500 text-white rounded-md' onClick={() => setShowBillModal(false)}>
							Close
						</button>
					</div>
				</div>

				{/* Invoice Body */}
				<div ref={billRef} className='p-8 bg-white'>
					<div className='flex justify-between items-start'>
						{/* Company Details */}
						<div className='text-left'>
							<h1 className='text-2xl font-bold text-blue-600'>Necttos OPC Private Limited</h1>
							<p className='text-gray-600'>1st Floor, Ongallur</p>
							<p className='text-gray-600'>Pattambi Road</p>
							<p className='text-gray-600'>Palakkad District, Kerala - 679313</p>
							<p className='text-gray-600'>CIN: DSFDFREREWRWERSDFSDSD</p>

							<p className='mt-2 font-semibold text-gray-700'>BILLED TO:</p>
							<p className='text-gray-600'>{invoiceData?.managedBy || 'Client Name'}</p>
							<p className='text-gray-600'>Street Address</p>
							<p className='text-gray-600'>City, State, ZIP Code</p>
							<p className='text-gray-600'>Phone</p>
						</div>

						{/* Logo & Contact */}
						<div className='flex flex-col items-end'>
							<img src='https://firebasestorage.googleapis.com/v0/b/necttos-production.appspot.com/o/Screenshot%202024-07-29%20at%2010.14.17%20AM.png?alt=media&token=15b88553-3b1c-4d7c-94af-7a6a4baf1046' alt='Company Logo' className='w-[120px] h-[120px] object-contain rounded-md shadow-md' />
							<p className='text-gray-700 text-sm mt-2'>contact@necttos.com</p>
							<p className='text-blue-600 underline cursor-pointer'>necttos.com</p>
							<p className='text-gray-700 font-semibold'>9400001733</p>
						</div>
					</div>

					{/* Invoice Numbers & Date */}
					<div className='grid grid-cols-3 gap-6 border-b pb-5 mb-6 text-gray-700'>
						<div>
							<p className='font-semibold'>INVOICE NUMBER</p>
							<p>#{invoiceData?.invoiceNumber || '00001'}</p>
						</div>
						<div>
							<p className='font-semibold'>DATE OF ISSUE</p>
							<p>{new Date().toLocaleDateString()}</p>
						</div>
						<div>
							<p className='font-semibold'>PO BILL NUMBER</p>
							<p>#00002</p>
						</div>
						<div>
							<p className='font-semibold'>E-WAY BILL NUMBER</p>
							<p>#00003</p>
						</div>
					</div>

					{/* Invoice Table */}
					<div className='overflow-x-auto'>
						<table className='w-full border border-gray-300 text-gray-800'>
							<thead className='bg-gray-200'>
								<tr>
									<th className='border p-3 text-left'>DESCRIPTION</th>
									<th className='border p-3 text-right'>UNIT COST</th>
									<th className='border p-3 text-right'>QTY</th>
									<th className='border p-3 text-right'>AMOUNT</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td className='border p-3'>Subscription Fee</td>
									<td className='border p-3 text-right'>₹400</td>
									<td className='border p-3 text-right'>{invoiceData?.value || 1}</td>
									<td className='border p-3 text-right'>₹{(invoiceData?.value || 1) * 400}</td>
								</tr>
							</tbody>
						</table>
					</div>

					{/* Summary Section */}
					<div className='mt-6 grid grid-cols-2 gap-6 text-gray-700'>
						<div>
							<p className='font-semibold'>TERMS</p>
							<p>Please pay invoice by {new Date().toLocaleDateString()}</p>
							<p className='mt-3 font-semibold'>BANK ACCOUNT DETAILS</p>
							<p>Account Holder: Your Name</p>
							<p>Account Number: 1234567890</p>
							<p>IFSC Code: XYZ0001234</p>
						</div>
						<div className='text-right'>
							<p className='font-semibold'>SUBTOTAL: ₹{(invoiceData?.value || 1) * 400}</p>
							<p>DISCOUNT: ₹0.00</p>
							<p>(TAX RATE) 0%</p>
							<p className='font-semibold'>TAX: ₹0.00</p>
							<p className='text-2xl font-bold text-blue-600 mt-2'>INVOICE TOTAL: ₹{(invoiceData?.value || 1) * 400}</p>
						</div>
					</div>

					{/* Footer */}
					<div className='mt-8 text-gray-600 text-center text-sm border-t pt-4'>
						<p>
							Thank you for choosing Necttos! Reach us at <strong>support@necttos.com</strong>
						</p>
						<p className='italic'>This is a system-generated invoice, no signature required.</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Invoice;
